import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { hasMinRole, useAuth } from "@/contexts/AuthContext";
import {
  ShieldWarning, Warning, GitBranch, Plus, ArrowUp, CheckCircle,
  XCircle, Clock, Flag,
} from "@phosphor-icons/react";

const SEV_TONE = {
  low: "bg-emerald-100 text-emerald-700 border-emerald-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  critical: "bg-red-100 text-red-800 border-red-200",
};

const HEATMAP_COLOR = (l, i) => {
  const sev = l * i;
  if (sev >= 16) return "bg-red-500";
  if (sev >= 11) return "bg-orange-400";
  if (sev >= 5) return "bg-amber-300";
  return "bg-emerald-300";
};

const PRIORITY_TONE = {
  critical: "bg-red-100 text-red-800",
  major: "bg-orange-100 text-orange-800",
  low: "bg-slate-100 text-slate-700",
};

const CR_STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700",
  submitted: "bg-blue-100 text-blue-700",
  under_review: "bg-indigo-100 text-indigo-700",
  approved: "bg-emerald-100 text-emerald-700",
  approved_with_conditions: "bg-amber-100 text-amber-800",
  rejected: "bg-red-100 text-red-700",
  withdrawn: "bg-slate-200 text-slate-500",
};

export default function ProjectRiskIssueChange({ project, trigger }) {
  const { user } = useAuth();
  const canEdit = hasMinRole(user, "manager");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("risks");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid={`ric-${project.id}`}>
            <ShieldWarning size={12} className="mr-1" /> Risk · Issue · CR
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="ric-drawer">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldWarning size={18} className="text-amber-600" />
            Risk · Issue · Change Control · {project.name}
          </DialogTitle>
          <DialogDescription>
            F-PM-25 Risk Register · F-PM-26 Issue Log · F-PM-07 Change Requests
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-slate-200">
          {[
            ["risks", "Risk Register", ShieldWarning],
            ["issues", "Issue Log", Warning],
            ["crs", "Change Requests", GitBranch],
          ].map(([id, label, Icon]) => (
            <button
              key={id} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition ${tab === id ? "border-[#1E5CBA] text-[#1E5CBA]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`ric-tab-${id}`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {tab === "risks" && <RiskTab project={project} canEdit={canEdit} />}
        {tab === "issues" && <IssueTab project={project} canEdit={canEdit} />}
        {tab === "crs" && <ChangeRequestTab project={project} canEdit={canEdit} />}
      </DialogContent>
    </Dialog>
  );
}

/* ─── RISK ──────────────────────────────────────────────────────────── */
function RiskTab({ project, canEdit }) {
  const [risks, setRisks] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/risks?project_id=${project.id}`),
        api.get(`/risks/heatmap?project_id=${project.id}`),
      ]);
      setRisks(a.data.items || []);
      setHeatmap(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4 pt-4">
      {/* Bands strip + heatmap */}
      {heatmap && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="rounded-xl border border-slate-200 bg-white p-3 lg:col-span-2">
            <div className="overline mb-2">Likelihood × Impact heatmap (open risks only)</div>
            <div className="flex items-end gap-1">
              <div className="flex flex-col-reverse items-center gap-1 text-[9px] font-mono-alt text-slate-500 mr-1">
                <div className="h-8" />
                {[1, 2, 3, 4, 5].map((l) => (
                  <div key={l} className="h-8 flex items-center">{l}</div>
                ))}
                <div className="font-semibold mt-1">L</div>
              </div>
              <div className="flex-1">
                <div className="grid grid-cols-5 gap-1">
                  {[5, 4, 3, 2, 1].flatMap((l) =>
                    [1, 2, 3, 4, 5].map((i) => {
                      const cell = heatmap.grid?.[l - 1]?.[i - 1] || { count: 0 };
                      return (
                        <div
                          key={`${l}-${i}`}
                          className={`h-8 rounded text-xs font-bold flex items-center justify-center text-white ${HEATMAP_COLOR(l, i)} ${cell.count ? "opacity-100" : "opacity-30"}`}
                          title={`L${l} × I${i} = ${l * i} · ${cell.count} risk(s)`}
                        >
                          {cell.count || ""}
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="mt-1 grid grid-cols-5 gap-1 text-[9px] font-mono-alt text-slate-500 text-center">
                  {[1, 2, 3, 4, 5].map((i) => <div key={i}>{i}</div>)}
                </div>
                <div className="text-center text-[9px] font-mono-alt text-slate-500 font-semibold mt-0.5">IMPACT →</div>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="overline mb-2">Severity bands</div>
            {["critical", "high", "medium", "low"].map((b) => (
              <div key={b} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${SEV_TONE[b]}`}>{b}</span>
                <span className="font-display text-lg font-semibold tabular-nums">{heatmap.band_counts?.[b] || 0}</span>
              </div>
            ))}
            <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] text-slate-500">
              Total open · <strong>{heatmap.total_open}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Header + create */}
      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold">{risks.length} risks logged</h4>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => { setEdit(null); setShowForm(true); }} data-testid="risk-create-btn">
            <Plus size={11} className="mr-1" /> Add risk
          </Button>
        )}
      </div>

      {loading ? <Loading /> : risks.length === 0 ? <Empty text="No risks logged for this project yet." /> : (
        <div className="dc-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-3 py-2">Risk</th>
                <th className="px-3 py-2">Cat</th>
                <th className="px-3 py-2">L×I</th>
                <th className="px-3 py-2">Severity</th>
                <th className="px-3 py-2">Treatment</th>
                <th className="px-3 py-2">Owner</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {risks.map((r) => (
                <tr key={r.id} onClick={() => { setEdit(r); setShowForm(true); }} className="cursor-pointer hover:bg-slate-50" data-testid={`risk-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-900">{r.title}</div>
                    {r.mitigation_plan && <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">{r.mitigation_plan}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500 font-mono-alt">{r.category}</td>
                  <td className="px-3 py-2 font-mono-alt text-xs">{r.likelihood}×{r.impact}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider ${SEV_TONE[r.severity_band]}`}>
                      {r.severity} · {r.severity_band}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">{r.treatment_strategy}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.owner_email || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.status?.replace(/_/g, " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <RiskForm
          projectId={project.id}
          risk={edit}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function RiskForm({ projectId, risk, onClose, onSaved }) {
  const isEdit = !!risk?.id;
  const [f, setF] = useState(risk || {
    title: "", description: "", cause: "", impact_text: "",
    category: "technical", likelihood: 3, impact: 3,
    treatment_strategy: "mitigate", mitigation_plan: "",
    owner_email: "", due_date: "", status: "open",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...f, project_id: projectId,
        likelihood: Number(f.likelihood), impact: Number(f.impact) };
      if (isEdit) await api.patch(`/risks/${risk.id}`, payload);
      else await api.post("/risks", payload);
      toast.success(isEdit ? "Risk updated" : "Risk added");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const sev = (Number(f.likelihood) || 0) * (Number(f.impact) || 0);
  const band = sev >= 16 ? "critical" : sev >= 11 ? "high" : sev >= 5 ? "medium" : "low";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit risk" : "New risk"}</DialogTitle>
          <DialogDescription>F-PM-25 Risk Register entry</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><Label>Title *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="risk-title" /></div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div><Label>Cause</Label><Textarea rows={2} value={f.cause} onChange={(e) => setF({ ...f, cause: e.target.value })} /></div>
          <div><Label>Impact text</Label><Textarea rows={2} value={f.impact_text} onChange={(e) => setF({ ...f, impact_text: e.target.value })} /></div>
          <div>
            <Label>Category</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["technical", "commercial", "schedule", "safety", "quality", "regulatory", "external", "hr", "environmental"].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Treatment</Label>
            <Select value={f.treatment_strategy} onValueChange={(v) => setF({ ...f, treatment_strategy: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="avoid">Avoid</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="mitigate">Mitigate</SelectItem>
                <SelectItem value="accept">Accept</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Likelihood (1-5)</Label><Input type="number" min={1} max={5} value={f.likelihood} onChange={(e) => setF({ ...f, likelihood: e.target.value })} data-testid="risk-likelihood" /></div>
          <div><Label>Impact (1-5)</Label><Input type="number" min={1} max={5} value={f.impact} onChange={(e) => setF({ ...f, impact: e.target.value })} data-testid="risk-impact" /></div>
          <div className="col-span-2 rounded-lg border bg-slate-50 p-2 text-xs">
            Calculated severity ·
            <span className={`ml-2 inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${SEV_TONE[band]}`}>{sev} · {band}</span>
          </div>
          <div className="col-span-2"><Label>Mitigation plan</Label><Textarea rows={2} value={f.mitigation_plan} onChange={(e) => setF({ ...f, mitigation_plan: e.target.value })} /></div>
          <div><Label>Owner email</Label><Input value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} /></div>
          <div><Label>Due date</Label><Input type="date" value={f.due_date || ""} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
          {isEdit && (
            <div className="col-span-2">
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["open", "in_progress", "mitigated", "closed", "realised"].map((s) => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !f.title} className="btn-primary-blue" onClick={submit} data-testid="risk-save">
            {busy ? "Saving…" : isEdit ? "Update" : "Add risk"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── ISSUE ─────────────────────────────────────────────────────────── */
function IssueTab({ project, canEdit }) {
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState({ total: 0, breached: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [escalate, setEscalate] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/issues?project_id=${project.id}`);
      setIssues(data.items || []);
      setStats({ total: data.total, breached: data.breached });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-3 gap-3">
        <KPI label="Total issues" value={stats.total} />
        <KPI label="SLA breached" value={stats.breached} tone="red" />
        <KPI label="Critical/Major"
              value={issues.filter((i) => i.priority !== "low").length} tone="amber" />
      </div>

      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold">{stats.total} issues</h4>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => { setEdit(null); setShowForm(true); }} data-testid="issue-create-btn">
            <Plus size={11} className="mr-1" /> Log issue
          </Button>
        )}
      </div>

      {loading ? <Loading /> : issues.length === 0 ? <Empty text="No issues logged for this project yet." /> : (
        <div className="dc-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-3 py-2">Issue</th>
                <th className="px-3 py-2">Priority</th>
                <th className="px-3 py-2">Owner</th>
                <th className="px-3 py-2">Target</th>
                <th className="px-3 py-2">Age</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {issues.map((i) => (
                <tr key={i.id} className={`hover:bg-slate-50 ${i.sla_breached ? "bg-red-50/40" : ""}`} data-testid={`issue-row-${i.id}`}>
                  <td className="px-3 py-2 cursor-pointer" onClick={() => { setEdit(i); setShowForm(true); }}>
                    <div className="font-semibold text-slate-900 flex items-center gap-2">
                      {i.title}
                      {i.sla_breached && <Flag size={11} weight="fill" className="text-red-500" />}
                    </div>
                    {i.description && <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">{i.description}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${PRIORITY_TONE[i.priority]}`}>{i.priority}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{i.owner_email || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono-alt">{i.target_resolution_date}</td>
                  <td className={`px-3 py-2 text-xs font-mono-alt ${i.sla_breached ? "text-red-600 font-semibold" : "text-slate-600"}`}>{i.age_days}d</td>
                  <td className="px-3 py-2 text-xs">{i.status?.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && i.status !== "closed" && i.status !== "resolved" && i.status !== "escalated" && (
                      <Button size="sm" variant="outline" onClick={() => setEscalate(i)} data-testid={`escalate-${i.id}`}>
                        <ArrowUp size={11} className="mr-1" /> Escalate
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <IssueForm projectId={project.id} issue={edit} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {escalate && <EscalateDialog issue={escalate} onClose={() => setEscalate(null)} onDone={() => { setEscalate(null); load(); }} />}
    </div>
  );
}

function IssueForm({ projectId, issue, onClose, onSaved }) {
  const isEdit = !!issue?.id;
  const [f, setF] = useState(issue || {
    title: "", description: "", priority: "major",
    owner_email: "", target_resolution_date: "",
    status: "open", resolution_notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...f };
      if (isEdit) await api.patch(`/issues/${issue.id}`, payload);
      else await api.post("/issues", { ...payload, project_id: projectId });
      toast.success(isEdit ? "Issue updated" : "Issue logged");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit issue" : "Log issue"}</DialogTitle>
          <DialogDescription>F-PM-26 Issue Log entry · SLA auto-assigned by priority</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label>Title *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="issue-title" /></div>
          <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger data-testid="issue-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical (SLA 5 wd)</SelectItem>
                  <SelectItem value="major">Major (SLA 10 wd)</SelectItem>
                  <SelectItem value="low">Low (SLA 20 wd)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Owner email</Label><Input value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} /></div>
          </div>
          <div><Label>Target resolution date (auto-set if blank)</Label><Input type="date" value={f.target_resolution_date || ""} onChange={(e) => setF({ ...f, target_resolution_date: e.target.value })} /></div>
          {isEdit && (
            <>
              <div>
                <Label>Status</Label>
                <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["open", "in_progress", "resolved", "escalated", "closed"].map((s) => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Resolution notes</Label><Textarea rows={2} value={f.resolution_notes || ""} onChange={(e) => setF({ ...f, resolution_notes: e.target.value })} /></div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !f.title} className="btn-primary-blue" onClick={submit} data-testid="issue-save">
            {busy ? "Saving…" : isEdit ? "Update" : "Log issue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EscalateDialog({ issue, onClose, onDone }) {
  const [to, setTo] = useState(issue.age_days > 20 ? "coo@descon.group" : "pd@descon.group");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/issues/${issue.id}/escalate`, { escalated_to: to, note });
      toast.success(`Escalated to ${to}`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Escalate issue</DialogTitle>
          <DialogDescription>{issue.title} · age {issue.age_days}d</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label>Escalate to (email or role)</Label><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="pd@descon.group" /></div>
          <div><Label>Note</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <div className="rounded bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800">
            {issue.age_days > 20
              ? "SOP triggers COO escalation when open >20 wd."
              : issue.age_days > 10
                ? "SOP triggers PD escalation when open >10 wd."
                : "Early escalation — provide context for the recipient."}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !to} className="btn-primary-blue" onClick={submit} data-testid="escalate-submit">
            {busy ? "Sending…" : "Escalate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── CHANGE REQUEST ─────────────────────────────────────────────────── */
function ChangeRequestTab({ project, canEdit }) {
  const { user } = useAuth();
  const canApprove = hasMinRole(user, "admin");
  const [crs, setCRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/change-requests?project_id=${project.id}`);
      setCRs(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const transition = async (id, decision, comment = "") => {
    try {
      await api.post(`/change-requests/${id}/transition`, { decision, comment });
      toast.success(`CR ${decision.replace(/_/g, " ")}`);
      setSelected(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold">{crs.length} change requests</h4>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => { setEdit(null); setShowForm(true); }} data-testid="cr-create-btn">
            <Plus size={11} className="mr-1" /> New CR
          </Button>
        )}
      </div>

      {loading ? <Loading /> : crs.length === 0 ? <Empty text="No change requests yet." /> : (
        <div className="dc-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-3 py-2">CR #</th>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Cost impact</th>
                <th className="px-3 py-2">Schedule impact</th>
                <th className="px-3 py-2">DOA tier</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {crs.map((c) => (
                <tr key={c.id} onClick={() => setSelected(c)} className="cursor-pointer hover:bg-slate-50" data-testid={`cr-row-${c.id}`}>
                  <td className="px-3 py-2 font-mono-alt text-xs font-semibold">{c.cr_number}</td>
                  <td className="px-3 py-2">{c.title}</td>
                  <td className="px-3 py-2 text-xs">{c.change_type}</td>
                  <td className="px-3 py-2 font-mono-alt tabular-nums text-xs">{formatCurrency(c.impact_cost_usd)}</td>
                  <td className="px-3 py-2 font-mono-alt text-xs">{c.impact_schedule_days}d</td>
                  <td className="px-3 py-2 text-xs">T{c.required_tier} · {c.required_role}</td>
                  <td className="px-3 py-2"><span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${CR_STATUS_TONE[c.status]}`}>{c.status?.replace(/_/g, " ")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <CRForm projectId={project.id} cr={edit} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}

      {selected && (
        <Dialog open onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <GitBranch size={16} /> {selected.cr_number} — {selected.title}
              </DialogTitle>
              <DialogDescription>
                {selected.change_type} · Tier {selected.required_tier} ({selected.required_role}) ·
                <span className={`ml-2 inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${CR_STATUS_TONE[selected.status]}`}>
                  {selected.status?.replace(/_/g, " ")}
                </span>
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <KV label="Cost impact">{formatCurrency(selected.impact_cost_usd)}</KV>
              <KV label="Schedule impact">{selected.impact_schedule_days}d</KV>
              <KV label="% of budget">{selected.impact_pct_budget != null ? `${selected.impact_pct_budget}%` : "—"}</KV>
              <KV label="Requested by">{selected.requested_by}</KV>
              <div className="col-span-2"><KV label="Description">{selected.description || "—"}</KV></div>
              <div className="col-span-2"><KV label="Justification">{selected.justification || "—"}</KV></div>
              {selected.decision_comment && <div className="col-span-2"><KV label="Decision comment">{selected.decision_comment}</KV></div>}
            </div>
            <DialogFooter className="flex-wrap gap-2">
              {selected.status === "draft" && canEdit && (
                <Button onClick={() => transition(selected.id, "submit")}><CheckCircle size={11} className="mr-1" /> Submit</Button>
              )}
              {selected.status === "submitted" && canApprove && (
                <>
                  <Button className="btn-primary-blue" onClick={() => transition(selected.id, "approve", "Approved")}><CheckCircle size={11} className="mr-1" /> Approve</Button>
                  <Button variant="destructive" onClick={() => transition(selected.id, "reject", "Rejected")}><XCircle size={11} className="mr-1" /> Reject</Button>
                </>
              )}
              {(selected.status === "draft" || selected.status === "submitted") && canEdit && (
                <Button variant="ghost" onClick={() => transition(selected.id, "withdraw")}>Withdraw</Button>
              )}
              <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function CRForm({ projectId, cr, onClose, onSaved }) {
  const isEdit = !!cr?.id;
  const [f, setF] = useState(cr || {
    title: "", description: "", change_type: "scope",
    impact_scope: "", impact_schedule_days: 0, impact_cost_usd: 0,
    impact_quality: "", impact_risk: "", justification: "",
    alternatives: "", recommendation: "", client_approval_required: false,
  });
  const [busy, setBusy] = useState(false);
  const tier = Math.abs(Number(f.impact_cost_usd) || 0) <= 5000 ? 1
    : Math.abs(Number(f.impact_cost_usd) || 0) <= 25000 ? 2
    : Math.abs(Number(f.impact_cost_usd) || 0) <= 100000 ? 3 : 4;
  const tierRole = ["", "Project Manager", "Project Director", "Chief Operating Officer", "CEO / Managing Director"][tier];
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...f, project_id: projectId,
        impact_schedule_days: Number(f.impact_schedule_days),
        impact_cost_usd: Number(f.impact_cost_usd) };
      if (isEdit) await api.patch(`/change-requests/${cr.id}`, payload);
      else await api.post("/change-requests", payload);
      toast.success(isEdit ? "CR updated" : "CR created (draft)");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit CR" : "New change request"}</DialogTitle>
          <DialogDescription>F-PM-07 · DOA tier auto-set from cost impact</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><Label>Title *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="cr-title" /></div>
          <div>
            <Label>Type</Label>
            <Select value={f.change_type} onValueChange={(v) => setF({ ...f, change_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["scope", "schedule", "cost", "quality", "risk", "other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Cost impact (USD)</Label><Input type="number" value={f.impact_cost_usd} onChange={(e) => setF({ ...f, impact_cost_usd: e.target.value })} data-testid="cr-cost" /></div>
          <div><Label>Schedule impact (days)</Label><Input type="number" value={f.impact_schedule_days} onChange={(e) => setF({ ...f, impact_schedule_days: e.target.value })} /></div>
          <div className="col-span-2 rounded-lg border bg-slate-50 p-2 text-xs">
            Auto-detected DOA tier · <strong>Tier {tier}</strong> · approver role: <strong>{tierRole}</strong>
          </div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="col-span-2"><Label>Justification *</Label><Textarea rows={2} value={f.justification} onChange={(e) => setF({ ...f, justification: e.target.value })} /></div>
          <div><Label>Alternatives</Label><Textarea rows={2} value={f.alternatives} onChange={(e) => setF({ ...f, alternatives: e.target.value })} /></div>
          <div><Label>Recommendation</Label><Textarea rows={2} value={f.recommendation} onChange={(e) => setF({ ...f, recommendation: e.target.value })} /></div>
          <div><Label>Quality impact</Label><Input value={f.impact_quality} onChange={(e) => setF({ ...f, impact_quality: e.target.value })} /></div>
          <div><Label>Risk impact</Label><Input value={f.impact_risk} onChange={(e) => setF({ ...f, impact_risk: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !f.title || !f.justification} className="btn-primary-blue" onClick={submit} data-testid="cr-save">
            {busy ? "Saving…" : isEdit ? "Update" : "Save draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Small helpers */
const Loading = () => <div className="p-8 text-center text-sm text-slate-500">Loading…</div>;
const Empty = ({ text }) => <div className="dc-card p-10 text-center text-sm text-slate-500">{text}</div>;
const KV = ({ label, children }) => (
  <div><div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</div><div className="mt-1 text-slate-800">{children}</div></div>
);
const KPI = ({ label, value, tone = "slate" }) => {
  const palette = {
    slate: "bg-slate-50 text-slate-900 border-slate-200",
    red: "bg-red-50 text-red-900 border-red-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
  }[tone];
  return (
    <div className={`rounded-xl border p-3 ${palette}`}>
      <div className="text-[10px] font-mono-alt uppercase tracking-widest opacity-70">{label}</div>
      <div className="mt-1 font-display text-2xl font-semibold tabular-nums">{value ?? 0}</div>
    </div>
  );
};
